/**
 * MarketplaceDeployWizardModal — v2 flow (authed, no project context).
 *
 * The user clicked Deploy on a marketplace listing from the public
 * /marketplace route. They're signed in but don't have a project picked
 * yet. The detail modal closes and this wizard takes its place so the
 * visual identity carries over (same shell + close button + icon header).
 *
 * Two collapsing steps, no router transitions:
 *   1. Choose a project — list existing projects + inline "create new"
 *      form. 0-project users land straight in the create form. After
 *      pick (or create), the chosen project is checked.
 *   2. Workflow name — appears once a project is picked. Defaults to the
 *      template slug; shape-validated locally; server runs uniqueness
 *      check at deploy time and 409s with a suggested alternative on
 *      collision (same contract as the v3 deploy path).
 *
 * Footer is a single full-width Deploy button. Disabled until a project
 * is picked AND the name passes shape validation.
 *
 * Reuses .mkt-modal-* classes from MarketplaceDetailModal.css for the
 * shell so the wizard inherits the dark theme, close button, scroll
 * shadow, and footer treatment. Wizard-specific styles live alongside
 * in MarketplaceDeployWizardModal.css.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import {
  closeOutline,
  addOutline,
} from 'ionicons/icons';
import Dropdown from '../../components/atoms/Dropdown/Dropdown';
import MarketplaceWorkflowDeployFooter from './MarketplaceWorkflowDeployFooter';
import './MarketplaceDetailModal.css';
import './MarketplaceDeployWizardModal.css';

const MarketplaceDeployWizardModal = ({
  entry,
  isOpen,
  onClose,
  // Project list (already fetched by parent so we don't double-fetch on
  // re-mount). `projectsLoading` true means parent's GET is in flight.
  projects = [],
  projectsLoading = false,
  // Async — parent runs ProjectsService.createProject and resolves to
  // the new project { projectId, name, ... }. Throws on failure.
  onCreateProject,
  creatingProject = false,
  // Async — parent runs Marketplace.deployWorkflow, shows toast,
  // navigates to /projects/:id/pipelines/:type on success.
  onDeploy,
  deploying = false,
}) => {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  const [pickedProjectId, setPickedProjectId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  // Reset wizard state every time it (re-)opens so a fresh deploy
  // doesn't inherit a previous run's stale picks.
  useEffect(() => {
    if (!isOpen) {
      setPickedProjectId(null);
      setShowCreateForm(false);
      setNewProjectName('');
    }
  }, [isOpen]);

  // 0-project users skip straight to the create form — there's nothing
  // to pick from. Don't auto-flip if the user has explicitly cancelled
  // the form (no projects + cancel would re-open it instantly → loop).
  useEffect(() => {
    if (!isOpen || projectsLoading) return;
    if (projects.length === 0 && !pickedProjectId) {
      setShowCreateForm(true);
    }
  }, [isOpen, projectsLoading, projects.length, pickedProjectId]);

  // Esc closes + focus trap (mirrors the detail modal). Self-contained
  // so we don't share state with the detail modal's trap.
  useEffect(() => {
    if (!isOpen) return undefined;
    const node = dialogRef.current;
    if (!node) return undefined;

    const previouslyFocused = document.activeElement;
    const t = setTimeout(() => closeBtnRef.current?.focus(), 50);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = node.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, onClose]);

  const pickedProject = useMemo(
    () => projects.find((p) => p.projectId === pickedProjectId) || null,
    [projects, pickedProjectId]
  );

  const handleCreateAndPick = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim() || creatingProject) return;
    try {
      const created = await onCreateProject({ name: newProjectName.trim() });
      if (created?.projectId) {
        setPickedProjectId(created.projectId);
        setShowCreateForm(false);
        setNewProjectName('');
      }
    } catch (err) {
      // Parent surfaces the toast; nothing extra to do here.
    }
  };

  // The footer atom owns the name input + validation + warm-pool menu +
  // deploy button. It surfaces { workflowName, warm? } via onDeploy;
  // wrap that with the picked project id/name before calling our parent.
  const handleFooterDeploy = (opts) => {
    if (!pickedProjectId) return;
    // Mirror the in-project detail modal's gate: the footer raises
    // `_agentMissing` when the picked project has no default agent AND the
    // user hasn't picked one. Never deploy in that state — it has already
    // turned its own select red, and the backend would 400 (AGENT_REQUIRED)
    // anyway. Strip the helper flag before forwarding so the POST stays clean.
    if (opts?._agentMissing) return undefined;
    const { _agentMissing, ...rest } = opts || {};
    void _agentMissing;
    // Return the deploy promise so the footer can await it (inline key-entry
    // on 428 AGENT_KEY_MISSING).
    return onDeploy?.({
      projectId: pickedProjectId,
      projectName: pickedProject?.name,
      ...rest,
    });
  };

  if (!isOpen || !entry) return null;

  return (
    <div className="mkt-modal-overlay" onClick={onClose} role="presentation">
      <div
        ref={dialogRef}
        className="mkt-modal mkt-wizard"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mkt-wizard-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeBtnRef}
          type="button"
          className="mkt-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          <IonIcon icon={closeOutline} />
        </button>

        <div className="mkt-modal-body">
          <header className="mkt-modal-header">
            {entry.icon ? (
              // eslint-disable-next-line jsx-a11y/img-redundant-alt
              <img src={entry.icon} alt="" className="mkt-modal-icon" />
            ) : (
              <span className="mkt-modal-icon mkt-modal-icon--fallback">
                {(entry.name || entry.slug || '?').slice(0, 1).toUpperCase()}
              </span>
            )}
            <h2 id="mkt-wizard-title" className="mkt-modal-title">
              Deploy {entry.name || entry.slug}
            </h2>
            <p className="mkt-modal-tagline">
              Agents live inside projects. Pick an existing project — or
              create a new one — and we'll deploy this template into it.
            </p>
          </header>

          <section className="mkt-modal-section">
            <h3 className="mkt-modal-section-title">Choose a project</h3>

            {projectsLoading && projects.length === 0 ? (
              <div className="mkt-wizard-loading">
                <IonSpinner name="crescent" />
                <span>Loading your projects…</span>
              </div>
            ) : null}

            {projects.length > 0 && (
              <Dropdown
                className="mkt-wizard-project-dropdown"
                options={[
                  { key: '', label: 'Select a project…' },
                  ...projects.map((p) => ({ key: p.projectId, label: p.name })),
                ]}
                value={pickedProjectId || ''}
                onChange={(v) => {
                  setPickedProjectId(v || null);
                  setShowCreateForm(false);
                }}
              />
            )}

            {/* Create-new affordance. Collapses into an inline form when
                toggled; 0-project users see it pre-expanded. */}
            {!showCreateForm ? (
              <button
                type="button"
                className="mkt-wizard-create-toggle"
                onClick={() => {
                  setShowCreateForm(true);
                  setPickedProjectId(null);
                }}
              >
                <IonIcon icon={addOutline} aria-hidden="true" />
                <span>Create a new project</span>
              </button>
            ) : (
              <form onSubmit={handleCreateAndPick} className="mkt-wizard-create-form">
                <label className="mkt-wizard-create-label" htmlFor="mkt-wizard-new-project">
                  New project name
                </label>
                <div className="mkt-wizard-create-row">
                  <input
                    id="mkt-wizard-new-project"
                    type="text"
                    className="mkt-wizard-create-input"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    placeholder="my-project"
                    autoFocus
                    disabled={creatingProject}
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    className="mkt-wizard-create-submit"
                    disabled={creatingProject || !newProjectName.trim()}
                  >
                    {creatingProject ? 'Creating…' : 'Create'}
                  </button>
                  {projects.length > 0 && (
                    <button
                      type="button"
                      className="mkt-wizard-create-cancel"
                      onClick={() => {
                        setShowCreateForm(false);
                        setNewProjectName('');
                      }}
                      disabled={creatingProject}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            )}
          </section>

        </div>

        <footer className="mkt-modal-footer">
          {pickedProjectId ? (
            /* Reuse the atomic v3 footer (name field + warm-pool menu +
               split Deploy button). Identical look to the in-project
               detail modal's footer; the wizard contributes the picked
               projectId + projectName via its onDeploy wrapper. */
            <MarketplaceWorkflowDeployFooter
              entry={entry}
              projectId={pickedProjectId}
              projectName={pickedProject?.name}
              // Seed the picker from the picked project's default so a
              // project that HAS a default pre-selects it (and deploys
              // without forcing a re-pick). If the project has no default,
              // these are empty and the footer's "Pick an agent…" gate fires.
              projectAiAgent={pickedProject?.aiAgent}
              projectAiModel={pickedProject?.aiModel}
              deploying={deploying}
              onDeploy={handleFooterDeploy}
            />
          ) : (
            /* No project picked yet → disabled placeholder Deploy so the
               wizard footer doesn't suddenly grow tall when a project is
               selected. Click is intercepted by the disabled state. */
            <button
              type="button"
              className="mkt-modal-deploy mkt-modal-deploy--full"
              disabled
              aria-disabled="true"
              title="Pick or create a project first"
            >
              Deploy
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default MarketplaceDeployWizardModal;
